import { useQuery } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';

import {
  PaginationInput,
  ProjectCustomFieldAuditLogsQuery,
} from '@/gql/graphql';
import { CustomFieldDefinition } from '@payaca/custom-fields';

const GetProjectCustomFieldAuditLogs = graphql(`
  query ProjectCustomFieldAuditLogs(
    $projectId: ID!
    $identifier: String
    $pagination: PaginationInput
  ) {
    projectById(id: $projectId) {
      customField(identifier: $identifier) {
        field {
          schema
        }
        auditLogs(pagination: $pagination) {
          limit
          offset
          totalCount
          items {
            id
            prevValue
            value
            timestamp
            metadata {
              source {
                type
              }
            }
            field {
              schema
            }
            actingUser {
              __typename
              ... on MissingUser {
                message
              }
              ... on User {
                id
                fullName
                avatarUrl
                colour
                firstName
                lastName
                email
              }
            }
          }
        }
      }
    }
  }
`);

export type ProjectCustomFieldAuditLogsPage = NonNullable<
  NonNullable<
    ProjectCustomFieldAuditLogsQuery['projectById']['customField']
  >['auditLogs']
>;

export type ProjectCustomFieldAuditLog = NonNullable<
  ProjectCustomFieldAuditLogsPage['items'][number]
>;

export type ActingUser =
  ProjectCustomFieldAuditLogsPage['items'][number]['actingUser'];

const useGetProjectCustomFieldAuditLogs = (
  projectId: string,
  fieldIdentifier: string,
  pagination?: PaginationInput
) => {
  const { data, ...rest } = useQuery({
    enabled: !!projectId && !!fieldIdentifier,
    queryKey: projectKeys.customFieldAuditLogs(
      projectId,
      fieldIdentifier,
      pagination
    ),
    queryFn: () => {
      return gqlClient.request(GetProjectCustomFieldAuditLogs, {
        projectId,
        identifier: fieldIdentifier,
        pagination,
      });
    },
  });

  const schema = data?.projectById.customField?.field.schema;

  return {
    auditLogs: data?.projectById.customField?.auditLogs,
    field: schema ? CustomFieldDefinition.fromSchema(schema) : null,
    data,
    ...rest,
  };
};

export default useGetProjectCustomFieldAuditLogs;
